import {createElement, Fragment, forwardRef, memo, useMemo} from "react";
import {Link} from "react-router-dom";
import {XCol, XRow, XPush} from "../../components/xui";
import Ui from "../../components/ui2";
import UnMarkedown from "../../components/Markdown/UnMarkedown";
import {RevealSpinner, WithTooltip, cx, IconGateClosed, XText} from "@cdx/common";
import {Draggable} from "@codecks/dnd";
import FromNow from "../../components/FromNow";
import cardStyles from "../../components/Card/card.css";
import {CheckMark, MultiDragHint} from "../../components/Card";
import {ProjectTagTooltip, ProjectTagHint} from "../../components/Card/CardBottomHints";
import {statusForWorkflowItem} from "../../lib/card-status-utils";
import {useCardHoverHandlersWithSelection} from "../../components/Card/useHoverShortcuts";
import {rawEnrichCardProjectTags} from "../../components/Markdown/CardTags";
import {useInstance} from "../../lib/mate/mate-utils";
import {DependencyOverlay} from "../dependencies/DependencyArea";
import {cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import {CardBottom} from "../../components/Card/CardBottom";

const ageSteps = {fresh: 3600, medium: 43200};
const HourGlassTooltip = ({lastUpdateTime}) => (
  <XCol pa={1}>
    <XText preset="bold" size={0} color="active200">
      Last updated
    </XText>
    <XText size={2} color="active100">
      <FromNow date={lastUpdateTime} />
    </XText>
  </XCol>
);

const ItemDependencyOverlay = ({itemId}) => {
  const card = useInstance("workflowItem", itemId);
  return <DependencyOverlay card={card} isWorkflowItem />;
};

const BottomHints = ({masterTags, project, lastUpdateTime, hasDeps, itemId}) => {
  const enrichedTags = rawEnrichCardProjectTags({masterTags, project});
  const tagColors = enrichedTags.map((t) => t.color).filter(Boolean);
  const diffInSeconds = (new Date().getTime() - lastUpdateTime.getTime()) / 1000;

  const lefts = [
    diffInSeconds < ageSteps.medium ? (
      <WithTooltip
        tooltip={<HourGlassTooltip lastUpdateTime={lastUpdateTime} />}
        as={diffInSeconds < ageSteps.fresh ? Ui.Icon.HourglassFull : Ui.Icon.HourglassHalf}
        style={diffInSeconds < ageSteps.fresh ? {} : {opacity: 0.5}}
        className={cx(cardStyles.bottomHintIcons.md, cardStyles.bottomHintIcons.active)}
      />
    ) : null,
    hasDeps ? (
      <WithTooltip
        tooltip={<ItemDependencyOverlay itemId={itemId} />}
        options={{delayed: true, bg: "gray600", placement: "left"}}
        as={IconGateClosed}
        size="lg"
      />
    ) : null,
  ];

  const rights = [
    tagColors.length > 0 ? (
      <WithTooltip
        tooltip={<ProjectTagTooltip tags={enrichedTags} />}
        options={{bg: "gray600"}}
        as={ProjectTagHint}
        tagColors={tagColors}
      />
    ) : null,
  ];
  const leftComp = createElement(
    Fragment,
    {},
    ...lefts.filter(Boolean).slice(0, 4 - rights.filter(Boolean).length)
  );
  const rightComp = createElement(Fragment, {}, ...rights);
  return (
    <XRow sp={1} className={cardStyles.bottomHints.base} align="center">
      {leftComp}
      <XPush />
      {rightComp}
    </XRow>
  );
};

const DumbWorkflowItem = forwardRef((props, ref) => {
  const {bag, dragProps} = props;
  const {
    itemId,
    title,
    status,
    effort,
    priority,
    to,
    assignee,
    project,
    masterTags,
    isShown,
    checkboxStats,
    lastUpdateTime,
    setHoverInfo,
    isSelected,
    isSelecting,
    onSelect,
    onDeselect,
    onCloseItem,
    multiDragCount,
    hasDeps,
    canModify,
  } = bag;

  const {waitedForSelection, events} = useCardHoverHandlersWithSelection({
    type: "MINI_WORKFLOW_ITEM",
    isSelecting,
    cardId: itemId,
  });

  const handleSelectionClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSelected) {
      onDeselect(itemId);
    } else {
      onSelect(itemId);
    }
  };

  const handleClick = (e) => {
    if (isSelecting) {
      handleSelectionClick(e);
    } else if (isShown) {
      e.preventDefault();
      onCloseItem();
    }
  };

  const showSelectInterface = isSelecting || waitedForSelection;

  return (
    <div
      ref={ref}
      className={cx(cardStyles.backdrop.default, isSelected && cardStyles.backdrop.isSelected)}
      {...events}
    >
      <CheckMark
        bag={bag}
        handleSelectionClick={handleSelectionClick}
        showSelectInterface={showSelectInterface}
      />
      <XCol
        className={cx(
          cardStatusVariants[status],
          cardStyles.card.base,
          isShown && cardStyles.card.defaultHighlight,
          showSelectInterface && cardStyles.card.isReadyForSelection,
          isSelected && cardStyles.card.isSelected,
          isSelecting && cardStyles.card.isSelecting
        )}
        relative
      >
        {multiDragCount && (
          <XRow
            absolute
            inset="x"
            className={cardStyles.topHints}
            align="center"
            justify="end"
            px={0}
            sp={0}
          >
            <MultiDragHint count={multiDragCount} />
          </XRow>
        )}
        <XCol {...dragProps} onClick={handleClick} fillParent minHeight {...(to && {as: Link, to})}>
          <XCol
            relative
            fillParent
            className={cx(cardStyles.top.base, isShown && cardStyles.top.isShown)}
            bg="white"
            px={1}
            py={2}
            minHeight
          >
            <RevealSpinner size={40} show={title === null} withCover />
            <XCol relative fillParent minHeight>
              <XCol fillParent className={cardStyles.cardText.base} minHeight>
                {title && (
                  <UnMarkedown maxChars={70} projectId={project && project.id}>
                    {title}
                  </UnMarkedown>
                )}
              </XCol>
              <BottomHints
                masterTags={masterTags}
                project={project}
                lastUpdateTime={lastUpdateTime}
                hasDeps={hasDeps}
                itemId={itemId}
              />
            </XCol>
          </XCol>
          <CardBottom
            bag={{
              effort,
              priority,
              assignee,
              isShown,
              checkboxStats,
              setHoverInfo,
              cardId: itemId,
              type: "WORKFLOW_ITEM",
              cardContainerKey: "workflowItem",
              canModify,
            }}
          />
        </XCol>
      </XCol>
    </div>
  );
});

const InteractiveWorkflowItem = memo(
  forwardRef(({bag}, ref) => {
    const {itemId, isItemLoaded, dragged, canModify} = bag;
    if (dragged || !canModify) {
      return <DumbWorkflowItem bag={bag} ref={ref} />;
    } else {
      return (
        <Draggable type="WORKFLOW_ITEM" id={itemId} disabled={!isItemLoaded} mergeRef={ref}>
          {({handlers, ref: dragRef}) => (
            <DumbWorkflowItem bag={bag} dragProps={handlers} ref={dragRef} />
          )}
        </Draggable>
      );
    }
  })
);

const agesAgo = new Date(0);

/** @type any */
const WorkflowItem = forwardRef((props, ref) => {
  const {
    item,
    onCloseItem,
    itemUrl,
    isShown,
    otherIsShown,
    setHoverInfo,
    onNonMoveDragEnd,
    dragged,
    isSelected,
    isShiftHovered,
    isSelecting,
    onSelect,
    onDeselect,
    multiDragCount,
    canModify,
  } = props;
  const itemId = item.id;
  const targetDeck = item.targetDeck || item.deck;

  const project = targetDeck.project;
  const itemTitle = item.$meta.get("title", null);
  const isItemLoaded = item.$meta.isLoaded;

  const lastUpdateTime = item.$meta.get("lastUpdatedAt", agesAgo);

  const status = statusForWorkflowItem(item);
  const hasDeps = item.inDeps.length > 0 || item.outDeps.length > 0;

  const bag = useMemo(
    () => ({
      itemId,
      title: itemTitle,
      status,
      effort: item.effort,
      priority: item.priority,
      to: itemUrl,
      assignee: item.assignee,
      project: project,
      masterTags: item.masterTags,
      isShown,
      otherIsShown,
      checkboxStats: item.checkboxStats,
      lastUpdateTime,
      setHoverInfo,
      onCloseItem,
      isItemLoaded,
      onNonMoveDragEnd,
      dragged,
      isSelected,
      isShiftHovered,
      isSelecting,
      onSelect,
      onDeselect,
      multiDragCount,
      hasDeps,
      canModify,
    }),
    [
      isItemLoaded,
      isShown,
      item.assignee,
      item.checkboxStats,
      item.effort,
      item.priority,
      item.masterTags,
      itemId,
      itemTitle,
      lastUpdateTime,
      onCloseItem,
      otherIsShown,
      itemUrl,
      project,
      setHoverInfo,
      onNonMoveDragEnd,
      dragged,
      status,
      isSelected,
      isShiftHovered,
      isSelecting,
      onSelect,
      onDeselect,
      multiDragCount,
      hasDeps,
      canModify,
    ]
  );

  return <InteractiveWorkflowItem ref={ref} bag={bag} />;
});

export default WorkflowItem;
